import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import { FaRegBell } from "react-icons/fa6";
import { Dropdown, Badge, Button, Spinner, Alert, ListGroup } from 'react-bootstrap';
import translations from "./translations";
import { LanguageContext } from '../../contexts/LanguageProvider';

// --- Componente de Item de Notificação ---
const NotificationItem = ({ notification, isUnread, onMarkAsRead, t }) => (
  <ListGroup.Item 
    className="d-flex align-items-center"
    variant={isUnread ? "primary" : undefined}
    style={{ 
      borderLeft: isUnread ? '4px solid var(--bs-primary)' : 'none',
      paddingLeft: isUnread ? '12px' : '16px'
    }}
  >
    {/* Indicador de não lida */}
    {isUnread && (
      <span 
        className="me-2"
        style={{
          width: '8px',
          height: '8px',
          backgroundColor: 'var(--bs-primary)',
          borderRadius: '100%',
          position: 'absolute',
        }} 
        title="Não lida"
      />
    )}

    <div className="flex-grow-1" 
    style={{ marginLeft: isUnread ? '12px' : '0' }}>
      {notification.message}
    </div>

    {/* Botão para marcar como lida individualmente */}
    {isUnread && (
      <Button 
        variant="primary" 
        size="sm" 
        className="p-1 m-0"
        onClick={(e) => {
          e.stopPropagation();
          onMarkAsRead(notification.id);
        }}
      >
        {t.markAsRead}
      </Button>
    )}
  </ListGroup.Item>
);

// --- Componente Principal de Notificações ---
const NotificationDropdown = ({ align = "end", width = "350px" }) => {
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadIds, setUnreadIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.pt;

  // Função para buscar os dados da API
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const auth = localStorage.getItem('auth');
    const config = {
      headers: { Authorization: `Basic ${auth}` }
    };

    try {
      const [allResponse, unreadResponse] = await Promise.all([
        axios.get(API_ENDPOINTS.NOTIFICATION_BY_USER, config),
        axios.get(API_ENDPOINTS.NOTIFICATION_UNREAD, config)
      ]);

      setAllNotifications(allResponse.data);
      const unreadNotificationIds = unreadResponse.data.map(notif => notif.id);
      setUnreadIds(new Set(unreadNotificationIds));

    } catch (err) {
      // console.error("Erro ao buscar notificações:", err);
      setError(t.errorLoading);
    } finally {
      setIsLoading(false);
    }
  }, [t.errorLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Função para marcar uma notificação como lida
  const handleMarkAsRead = async (notificationId) => {
    const newUnreadIds = new Set(unreadIds);
    newUnreadIds.delete(notificationId);
    setUnreadIds(newUnreadIds);

    try {
      const auth = localStorage.getItem('auth');
      const url = API_ENDPOINTS.NOTIFICATION_MARK_AS_READ(notificationId);
      await axios.put(url, null, { headers: { Authorization: `Basic ${auth}` } });
    } catch (err) {
      // console.error("Erro ao marcar como lida:", err);
      const revertedUnreadIds = new Set(unreadIds);
      revertedUnreadIds.add(notificationId);
      setUnreadIds(revertedUnreadIds);
      alert(t.errorMarkingAsRead);
    }
  };

  // Função para marcar todas como lidas
  const handleMarkAllAsRead = async () => {
    setUnreadIds(new Set());

    try {
      const auth = localStorage.getItem('auth');
      await axios.put(API_ENDPOINTS.NOTIFICATION_MARK_ALL_AS_READ, null, { headers: { Authorization: `Basic ${auth}` } });
    } catch (err) {
      // console.error("Erro ao marcar todas como lidas:", err);
      fetchData();
      alert(t.errorMarkingAllAsRead);
    }
  };

  return (
    <Dropdown align={align}>
      <Dropdown.Toggle 
        variant="none" 
        id="notification-dropdown" 
        className="position-relative p-0 border-0 text-decoration-none"
        style={{ boxShadow: 'none' }}
      >
        <FaRegBell size={18} />
        {unreadIds.size > 0 && (
          <Badge 
            bg="danger" 
            pill 
            className="position-absolute translate-middle"
            style={{ fontSize: '0.65rem', left: '75%', top: '25%' }}
          >
            {unreadIds.size}
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu 
        style={{ 
          width: width, 
          maxHeight: '500px', 
          overflowY: 'auto' 
        }}
      >
        {/* Cabeçalho */}
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <h6 className="mb-0">{t.notifications}</h6>
          {unreadIds.size > 0 && (
            <Button 
              variant="link" 
              size="sm" 
              className="p-0 text-decoration-none"
              onClick={handleMarkAllAsRead}
            >
              {t.markAllAsRead}
            </Button>
          )}
        </div>

        {/* Conteúdo */}
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" role="status">
              <span className="visually-hidden">{t.loading}</span>
            </Spinner>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="m-3 mb-0">
            {error}
          </Alert>
        )}

        {!isLoading && !error && (
          <>
            {allNotifications.length > 0 ? (
              <ListGroup variant="flush">
                {allNotifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    isUnread={unreadIds.has(notification.id)}
                    onMarkAsRead={handleMarkAsRead}
                    t={t}
                  />
                ))}
              </ListGroup>
            ) : (
              <div className="text-center text-muted py-4">
                {t.noNotifications}
              </div>
            )}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;